import { notificationService } from './notificationService';


/**
 * Milestone thresholds for different metrics
 */
const MILESTONE_THRESHOLDS = {
  steps: [
    { value: 5000, title: 'Halfway Hero', emoji: '🚶', message: 'You\'ve reached 5,000 steps!' },
    { value: 10000, title: 'Step Master', emoji: '🏃', message: 'Amazing! 10,000 steps completed!' },
    { value: 15000, title: 'Step Legend', emoji: '⚡', message: 'Incredible! 15,000 steps achieved!' }
  ],
  calories: [
    { value: 1000, title: 'Calorie Tracker', emoji: '🔥', message: 'You\'ve logged 1,000 calories!' },
    { value: 2000, title: 'Nutrition Pro', emoji: '🥗', message: 'Great! 2,000 calories tracked!' },
    { value: 2500, title: 'Calorie Champion', emoji: '💪', message: 'Excellent! 2,500 calories logged!' }
  ],
  sleep: [
    { value: 6, title: 'Rest Starter', emoji: '😴', message: 'You got 6 hours of sleep!' },
    { value: 7, title: 'Sleep Achiever', emoji: '🌙', message: 'Nice! 7 hours of quality sleep!' },
    { value: 8, title: 'Sleep Champion', emoji: '✨', message: 'Perfect! 8 hours of sleep achieved!' }
  ],
  water: [
    { value: 4, title: 'Hydration Start', emoji: '💧', message: 'You\'ve had 4 glasses of water!' },
    { value: 6, title: 'Hydration Hero', emoji: '🌊', message: 'Great! 6 glasses of water!' },
    { value: 8, title: 'Hydration Master', emoji: '💎', message: 'Perfect! 8 glasses completed!' }
  ]
};

/**
 * Get storage key for milestone tracking
 */
const getMilestoneKey = (userId, metric, date) => {
  return `milestone_${userId}_${metric}_${date}`;
};

/**
 * Check if milestone was already triggered today
 */
const isMilestoneTriggered = (userId, metric, value, date) => {
  const key = getMilestoneKey(userId, metric, date);
  const triggered = localStorage.getItem(key);
  if (!triggered) return false;
  
  const triggeredValues = JSON.parse(triggered);
  return triggeredValues?.includes(value);
};

/**
 * Mark milestone as triggered
 */
const markMilestoneTriggered = (userId, metric, value, date) => {
  const key = getMilestoneKey(userId, metric, date);
  const triggered = localStorage.getItem(key);
  const triggeredValues = triggered ? JSON.parse(triggered) : [];
  
  if (!triggeredValues?.includes(value)) {
    triggeredValues?.push(value);
    localStorage.setItem(key, JSON.stringify(triggeredValues));
  }
};

/**
 * Clean up old milestone data (older than 7 days)
 */
const cleanupOldMilestones = () => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
  const cutoffDate = sevenDaysAgo?.toISOString()?.split('T')?.[0];
  
  Object.keys(localStorage)?.forEach(key => {
    if (key?.startsWith('milestone_')) {
      const parts = key?.split('_');
      const date = parts?.[parts?.length - 1];
      if (date < cutoffDate) {
        localStorage.removeItem(key);
      }
    }
  });
};

/**
 * Milestone Service
 */
export const milestoneService = {
  /**
   * Check if a metric value has crossed any milestone thresholds
   */
  checkMilestone(userId, metric, currentValue) {
    if (!userId || !metric || currentValue === null || currentValue === undefined) {
      return null;
    }

    const today = new Date()?.toISOString()?.split('T')?.[0];
    const thresholds = MILESTONE_THRESHOLDS?.[metric];
    
    if (!thresholds) return null;

    // Find the highest milestone that has been reached
    let achievedMilestone = null;
    for (const threshold of thresholds) {
      if (currentValue >= threshold?.value) {
        // Check if this milestone was already triggered today
        if (!isMilestoneTriggered(userId, metric, threshold?.value, today)) {
          achievedMilestone = threshold;
        }
      }
    }

    if (achievedMilestone) {
      // Mark as triggered
      markMilestoneTriggered(userId, metric, achievedMilestone?.value, today);
      
      // Clean up old data periodically
      if (Math.random() < 0.1) { // 10% chance on each check
        cleanupOldMilestones();
      }

      return {
        metric,
        ...achievedMilestone,
        value: currentValue,
        date: today
      };
    }

    return null;
  },

  /**
   * Check all metrics for milestones
   */
  checkAllMilestones(userId, metrics) {
    const milestones = [];
    
    if (metrics?.steps?.current) {
      const milestone = this.checkMilestone(userId, 'steps', metrics?.steps?.current);
      if (milestone) milestones?.push(milestone);
    }
    
    if (metrics?.calories?.current) {
      const milestone = this.checkMilestone(userId, 'calories', metrics?.calories?.current);
      if (milestone) milestones?.push(milestone);
    }
    
    if (metrics?.sleep?.current) {
      const milestone = this.checkMilestone(userId, 'sleep', metrics?.sleep?.current);
      if (milestone) milestones?.push(milestone);
    }
    
    if (metrics?.water?.current) {
      const milestone = this.checkMilestone(userId, 'water', metrics?.water?.current);
      if (milestone) milestones?.push(milestone);
    }

    return milestones;
  },

  /**
   * Create notification for milestone achievement
   */
  async createMilestoneNotification(userId, milestone) {
    try {
      const metricLabels = {
        steps: 'Steps',
        calories: 'Calories',
        sleep: 'Sleep',
        water: 'Hydration'
      };

      const categoryColors = {
        steps: 'blue',
        calories: 'orange',
        sleep: 'purple',
        water: 'green'
      };

      await notificationService?.createNotification(userId, {
        type: 'activity_milestone',
        title: `${milestone?.emoji} ${milestone?.title}`,
        message: milestone?.message,
        categoryColor: categoryColors?.[milestone?.metric],
        priority: true,
        actionLabel: 'View Progress',
        actionPath: '/dashboard'
      });
    } catch (error) {
      console.error('Error creating milestone notification:', error);
    }
  },

  /**
   * Reset milestones for a specific date (for testing)
   */
  resetMilestones(userId, date) {
    const today = date || new Date()?.toISOString()?.split('T')?.[0];
    ['steps', 'calories', 'sleep', 'water']?.forEach(metric => {
      const key = getMilestoneKey(userId, metric, today);
      localStorage.removeItem(key);
    });
  }
};

export default milestoneService;